import { Component, HostListener } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '@app/auth/auth.service';
import { AppService } from '@app/app.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { User } from '@app/auth/user.model';
import { SupportFormDialogComponent } from '@app/account/support-form-dialog/support-form-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { InfoDialogComponent } from '@app/shared/info-dialog/info-dialog.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    RouterModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  user: User | null = null;
  carrierConnectionCount: number | null = null;

  isMobile = false;
  isCompactHeader = false;

  pageTitle: string = 'Wide & Heavy';

  allNavRoutes = [
    {
      label: 'Search Loads',
      link: '/loads/searches',
      title: 'Search Loads',
      icon: 'search',
      permission: 'can_search_loads',
    },
    {
      label: 'Saved Loads',
      link: '/loads/saved',
      title: 'Saved Loads',
      icon: 'favorite_border',
      permission: 'can_search_loads',
    },
    {
      label: 'My Trucks',
      link: '/trucks',
      title: 'My Trucks',
      icon: 'add_circle_outline',
      permission: 'can_post_trucks',
    },
    {
      label: 'Search Trucks',
      link: '/trucks/searches',
      title: 'Search Trucks',
      icon: 'search',
      permission: 'can_search_trucks',
    },
    {
      label: 'Saved Trucks',
      link: '/trucks/saved',
      title: 'Saved Trucks',
      icon: 'favorite_border',
      permission: 'can_search_trucks',
    },
    {
      label: 'My Loads',
      link: '/loads',
      title: 'My Loads',
      icon: 'add_circle_outline',
      permission: 'can_post_loads',
    },
  ];

  navRoutes = [...this.allNavRoutes];

  settingsRoutes = [
    {
      label: 'Account',
      link: '/account',
      title: 'Account',
      icon: 'account_circle',
    },
    {
      label: 'Connections',
      link: '/account/connections',
      title: 'Connections',
      icon: 'people',
    },
    {
      label: 'Settings',
      link: '/account/settings',
      title: 'Settings',
      icon: 'settings',
    },
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
    private appService: AppService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
  ) {
    this.checkScreenSize();
    this.processRoutes();

    this.appService.headerRefresh$.subscribe(() => {
      // Refresh user data and routes when header refresh is triggered
      this.processRoutes();
    });

    this.router.events.subscribe(() => {
      this.pageTitle = this.getPageTitle(this.route);
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    const width = window.innerWidth;

    this.isMobile = width < 768;
    this.isCompactHeader = width < 1280;
  }

  isActive(route: string): boolean {
    return this.router.url.split('?')[0] === route;
  }

  logout(): void {
    this.appService.confirm('Are you sure you want to log out?').subscribe(result => {
      if (result) {
        this.authService.logout();
      }
    });
  }

  getUserFromStorage(): void {
    this.user = this.authService.getUserFromStorage();
  }

  processRoutes(): void {
    this.user = this.authService.getUserFromStorage();

    this.carrierConnectionCount = this.user?.carriers?.length || 0;

    this.navRoutes = this.allNavRoutes.filter(route => {
      return !route.permission || Boolean(this.user?.[route.permission as keyof User]);
    });
  }

  openSupportDialog(): void {
    this.dialog.open(SupportFormDialogComponent, {
      width: '400px',
    });
  }

  getPageTitle(route: ActivatedRoute): string {
    let current = route;

    while (current.firstChild) {
      current = current.firstChild;
    }

    return current.snapshot.data['title'] || '';
  }

  hasNoCarrierConnections(): boolean {
    return this.carrierConnectionCount === 0;
  }

  openConnectionsInfo(): void {
    this.dialog.open(InfoDialogComponent, {
      width: '400px',
      data: {
        message: 'You currently have no carrier connections. To post or search loads and trucks, please connect with a carrier or broker. Go to the Connections section to join a carrier or broker.',
        message_warning: true,
      },
    });
  }
}